import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ErrorState } from '../../../components/ui/States';
import { DataNotes, InfoTip } from '../../../components/ui/InfoTip';
import type { FinanceDataIssue } from '../../../api/finance';

/**
 * Every finance endpoint carries the caveats behind its own numbers. Warnings
 * are diagnostics about the data (sync gaps, reconciliation differences), so
 * they collect into one muted "N data notes" chip in the Finance page header;
 * an `error` is a real failure and still renders in place as an ErrorState.
 */

type Report = (source: string, issues: FinanceDataIssue[]) => void;

const ReportContext = createContext<Report | null>(null);
const NotesContext = createContext<FinanceDataIssue[]>([]);

export function FinanceIssuesProvider({ children }: { children: ReactNode }) {
  const [bySource, setBySource] = useState<Record<string, FinanceDataIssue[]>>({});
  const report = useCallback<Report>(
    (source, issues) => setBySource((prev) => ({ ...prev, [source]: issues })),
    [],
  );
  const notes = useMemo(() => {
    // The same caveat can come back from two endpoints; say it once.
    const seen = new Set<string>();
    return Object.values(bySource)
      .flat()
      .filter((i) => i.severity !== 'error' && !seen.has(i.message) && seen.add(i.message));
  }, [bySource]);
  return (
    <ReportContext.Provider value={report}>
      <NotesContext.Provider value={notes}>{children}</NotesContext.Provider>
    </ReportContext.Provider>
  );
}

/**
 * Sends a panel's warnings to the page-header chip while the panel is mounted.
 * Outside a Finance page (no provider) it returns false, and the caller shows
 * them itself — a caveat is never dropped.
 */
export function useFinanceIssues(source: string, issues: FinanceDataIssue[] | undefined): boolean {
  const report = useContext(ReportContext);
  const key = JSON.stringify(issues ?? []);
  useEffect(() => {
    if (!report) return;
    report(source, JSON.parse(key) as FinanceDataIssue[]);
    return () => report(source, []);
  }, [report, source, key]);
  return report !== null;
}

/** The header chip with every warning the mounted panels reported. */
export function FinanceDataNotes() {
  const notes = useContext(NotesContext);
  return <DataNotes notes={notes.map((n) => n.message)} />;
}

/** Errors in place; warnings as a local chip (for panels outside the page header's reach). */
export function DataIssues({ issues, showWarnings = false }: { issues: FinanceDataIssue[] | undefined; showWarnings?: boolean }) {
  if (!issues || issues.length === 0) return null;
  const errors = issues.filter((i) => i.severity === 'error');
  const warnings = issues.filter((i) => i.severity !== 'error');
  return (
    <>
      {errors.map((issue, idx) => (
        <ErrorState key={`${issue.code}-${idx}`} compact title={issue.message} />
      ))}
      {showWarnings && warnings.length > 0 && <DataNotes notes={warnings.map((w) => w.message)} />}
    </>
  );
}

/**
 * A card title with its method note behind an (i), instead of a caption under
 * the chart. `basis` is what the card measures, in its own words.
 */
export function PanelTitle({
  title,
  info,
  action,
}: {
  title: ReactNode;
  info?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-1.5 min-w-0">
        <h3 className="text-body-sm font-medium text-text-secondary">{title}</h3>
        {info && <InfoTip>{info}</InfoTip>}
      </div>
      {action}
    </div>
  );
}
